/**
 * Currency Utilities
 *
 * 다중 통화 지원을 위한 유틸리티 함수
 * 기준 통화: USD (달러) - DB에 저장된 가격 기준
 * 지원 통화: KRW, USD, JPY, CNY, EUR, TWD
 *
 * 환율 데이터: 한국수출입은행 API (1 외화 = X KRW)
 * 변환 방식: USD → KRW → 대상 통화
 */

// 지원 통화 타입
export type SupportedCurrency = "KRW" | "USD" | "JPY" | "CNY" | "EUR" | "TWD";

// 환율 정보 타입 (한국수출입은행 기준: 1 외화 = X KRW)
export interface ExchangeRate {
  code: string;
  name: string;
  /**
   * 고객 노출용 환율. 상품 결제 금액 표시·수령에 사용 (스프레드 포함).
   * `display_rate`가 있으면 그 값, 없으면 `marketRate`로 폴백.
   */
  rate: number;
  /**
   * 스프레드 없는 순수 시장 환율. 쿠폰 할인처럼 "플랫폼 face value와 정확히 일치해야 하는"
   * 계산에 사용. convertCurrency에 `useMarketRate: true`를 넘기면 이 값을 참조.
   */
  marketRate: number;
  rateDate: string;
  isFallback: boolean;
  updatedAt: string;
}

// 환율 데이터 타입
export type ExchangeRates = Record<SupportedCurrency, ExchangeRate>;

// 지원 통화 목록
export const SUPPORTED_CURRENCIES: SupportedCurrency[] = [
  "USD",
  "KRW",
  "JPY",
  "CNY",
  "EUR",
  "TWD",
];

// 통화 정보 (표시용)
export const CURRENCY_INFO: Record<
  SupportedCurrency,
  {
    code: SupportedCurrency;
    name: string;
    nameKo: string;
    symbol: string;
    flag: string;
    decimals: number; // 소수점 자릿수
  }
> = {
  USD: {
    code: "USD",
    name: "US Dollar",
    nameKo: "미국 달러",
    symbol: "$",
    flag: "🇺🇸",
    decimals: 2,
  },
  KRW: {
    code: "KRW",
    name: "Korean Won",
    nameKo: "원화",
    symbol: "₩",
    flag: "🇰🇷",
    decimals: 0,
  },
  JPY: {
    code: "JPY",
    name: "Japanese Yen",
    nameKo: "일본 엔",
    symbol: "¥",
    flag: "🇯🇵",
    decimals: 0,
  },
  CNY: {
    code: "CNY",
    name: "Chinese Yuan",
    nameKo: "중국 위안",
    symbol: "¥",
    flag: "🇨🇳",
    decimals: 2,
  },
  EUR: {
    code: "EUR",
    name: "Euro",
    nameKo: "유로",
    symbol: "€",
    flag: "🇪🇺",
    decimals: 2,
  },
  TWD: {
    code: "TWD",
    name: "Taiwan Dollar",
    nameKo: "대만 달러",
    symbol: "NT$",
    flag: "🇹🇼",
    decimals: 0,
  },
};

// Locale → Currency 매핑
export const LOCALE_CURRENCY_MAP: Record<string, SupportedCurrency> = {
  ko: "KRW",
  en: "USD",
  ja: "JPY",
  "zh-CN": "CNY",
  "zh-TW": "TWD",
};

/**
 * USD를 다른 통화로 변환
 *
 * 변환 경로: USD → KRW → 대상 통화
 * 환율 데이터는 한국수출입은행 기준 (1 외화 = X KRW)
 *
 * @param amountUSD - 달러 금액 (DB에 저장된 가격)
 * @param currency - 대상 통화
 * @param rates - 환율 데이터
 * @returns 변환된 금액
 * @deprecated convertCurrency 사용 권장 (상품별 통화 지원)
 */
export function convertFromUSD(
  amountUSD: number,
  currency: SupportedCurrency,
  rates: Partial<ExchangeRates>
): number {
  return convertCurrency(amountUSD, "USD", currency, rates);
}

/**
 * 소스 통화에서 대상 통화로 변환
 *
 * 변환 경로: 소스 통화 → KRW → 대상 통화
 * 환율 데이터는 한국수출입은행 기준 (1 외화 = X KRW)
 *
 * @param amount - 원본 금액 (상품의 기준 통화로 저장된 가격)
 * @param sourceCurrency - 상품의 기준 통화 (예: JPY)
 * @param targetCurrency - 사용자가 선택한 표시 통화 (예: KRW)
 * @param rates - 환율 데이터
 * @returns 변환된 금액
 */
export function convertCurrency(
  amount: number,
  sourceCurrency: SupportedCurrency,
  targetCurrency: SupportedCurrency,
  rates: Partial<ExchangeRates>,
  options?: { useMarketRate?: boolean }
): number {
  // 동일 통화면 변환 불필요
  if (sourceCurrency === targetCurrency) {
    return amount;
  }

  // 쿠폰 할인처럼 스프레드를 빼야 하는 계산은 marketRate를 쓴다.
  // marketRate가 없는 엔트리는 rate로 폴백(이전 캐시 호환).
  const pickRate = (entry: ExchangeRate | undefined): number | undefined => {
    if (!entry) return undefined;
    if (options?.useMarketRate && entry.marketRate > 0) return entry.marketRate;
    return entry.rate;
  };

  // 1. 소스 통화 → KRW 변환
  let amountKRW: number;
  if (sourceCurrency === "KRW") {
    amountKRW = amount;
  } else {
    const sourceRate = pickRate(rates[sourceCurrency]);
    if (!sourceRate || sourceRate <= 0) {
      // 환율 데이터가 아직 로드되지 않은 경우 (빈 객체)에는 경고 생략
      if (Object.keys(rates).length > 0) {
        console.warn(`Exchange rate for source currency ${sourceCurrency} not available`);
      }
      return amount; // 원본 반환
    }
    // 1 외화 = X KRW 이므로, 외화 금액 * 환율 = KRW 금액
    amountKRW = amount * sourceRate;
  }

  // 2. KRW → 대상 통화 변환
  if (targetCurrency === "KRW") {
    return amountKRW;
  }

  const targetRate = pickRate(rates[targetCurrency]);
  if (!targetRate || targetRate <= 0) {
    // 환율 데이터가 아직 로드되지 않은 경우 (빈 객체)에는 경고 생략
    if (Object.keys(rates).length > 0) {
      console.warn(`Exchange rate for target currency ${targetCurrency} not available`);
    }
    return amount; // 원본 반환
  }

  // KRW 금액 / 대상 환율 = 대상 통화 금액
  return amountKRW / targetRate;
}

/**
 * 결제 청구용 환율 변환 — spread를 **방향 무관**하게 고객에게 불리하게 적용.
 *
 * Rationale: `display_rate = market × (1 - spread)` 공식은 "KRW 상품 → 외화
 * 결제"일 때만 LOCARORA에 이득(고객이 시장가보다 더 많은 외화를 지불). 반대
 * 방향(외화 상품 → KRW 결제)에서는 자동으로 역전되어 고객이 시장가보다 덜
 * 지불하고 LOCARORA가 spread만큼 손해. 이 함수는 항상 시장가에서 spread만큼
 * 더 청구하는 방식으로 방향 편향을 없앤다.
 *
 * 공식: `charge = market_amount × (1 + max_spread)` — source/target 중 non-KRW
 * 통화의 스프레드(display_rate/market_rate 비율에서 역산)를 사용.
 *
 * **이 함수는 "고객에게 얼마를 청구할지"를 계산할 때만 사용.** 쿠폰 할인
 * face value 맞추기, 파트너 정산 기준금액 등 다른 목적엔 `convertCurrency` +
 * `useMarketRate` 조합을 쓸 것.
 */
export function convertForCharge(
  amount: number,
  sourceCurrency: SupportedCurrency,
  targetCurrency: SupportedCurrency,
  rates: Partial<ExchangeRates>
): number {
  if (sourceCurrency === targetCurrency) return amount;

  // 시장가로 먼저 변환
  const marketAmount = convertCurrency(
    amount,
    sourceCurrency,
    targetCurrency,
    rates,
    { useMarketRate: true },
  );

  // display_rate / market_rate 로부터 spread_pct 역산
  const impliedSpread = (entry: ExchangeRate | undefined): number => {
    if (!entry || entry.marketRate <= 0 || entry.rate <= 0) return 0;
    const s = 1 - entry.rate / entry.marketRate;
    return Math.max(0, s); // 음수 방지 (display > market인 이상 케이스 대비)
  };

  const spread = Math.max(
    impliedSpread(rates[sourceCurrency]),
    impliedSpread(rates[targetCurrency]),
  );

  return marketAmount * (1 + spread);
}

/**
 * 결제 청구 금액용 반올림 — 항상 **올림(ceil)**.
 *
 * `roundForCurrency`는 `Math.round`를 사용하므로 0.5 미만은 내림되어 고객에게
 * 유리(LOCARORA 손실). 결제 청구는 1원/1센트 이하 초과 청구가 허용되므로
 * ceil로 바꿔 LOCARORA 측 손실을 방지.
 */
export function roundForCharge(
  amount: number,
  currency: SupportedCurrency
): number {
  const decimals = CURRENCY_INFO[currency]?.decimals ?? 2;
  const factor = Math.pow(10, decimals);
  return Math.ceil(amount * factor) / factor;
}

/**
 * KRW를 외화로 변환 (레거시 호환용)
 * @deprecated convertFromUSD 사용 권장
 */
export function convertFromKRW(
  amountKRW: number,
  currency: SupportedCurrency,
  rate: number
): number {
  if (currency === "KRW") {
    return amountKRW;
  }

  if (!rate || rate <= 0) {
    console.warn(`Invalid exchange rate for ${currency}: ${rate}`);
    return amountKRW;
  }

  return amountKRW / rate;
}

/**
 * 외화를 KRW로 변환
 */
export function convertToKRW(
  amount: number,
  currency: SupportedCurrency,
  rate: number
): number {
  if (currency === "KRW") {
    return amount;
  }

  if (!rate || rate <= 0) {
    console.warn(`Invalid exchange rate for ${currency}: ${rate}`);
    return amount;
  }

  return amount * rate;
}

/**
 * 통화별 적절한 소수점으로 반올림
 */
export function roundForCurrency(
  amount: number,
  currency: SupportedCurrency
): number {
  const decimals = CURRENCY_INFO[currency]?.decimals ?? 2;
  const factor = Math.pow(10, decimals);
  return Math.round(amount * factor) / factor;
}

/**
 * 가격을 통화 형식으로 포맷
 */
export function formatPrice(
  amount: number,
  currency: SupportedCurrency,
  locale?: string,
  options?: Intl.NumberFormatOptions
): string {
  const currencyLocaleMap: Record<SupportedCurrency, string> = {
    KRW: "ko-KR",
    USD: "en-US",
    JPY: "ja-JP",
    CNY: "zh-CN",
    EUR: "de-DE",
    TWD: "zh-TW",
  };

  const targetLocale = locale || currencyLocaleMap[currency] || "en-US";
  const roundedAmount = roundForCurrency(amount, currency);

  try {
    return new Intl.NumberFormat(targetLocale, {
      style: "currency",
      currency,
      minimumFractionDigits: CURRENCY_INFO[currency]?.decimals ?? 0,
      maximumFractionDigits: CURRENCY_INFO[currency]?.decimals ?? 2,
      ...options,
    }).format(roundedAmount);
  } catch {
    const symbol = CURRENCY_INFO[currency]?.symbol || currency;
    return `${symbol}${roundedAmount.toLocaleString()}`;
  }
}

/**
 * 간단한 가격 포맷 (통화 기호 + 숫자)
 */
export function formatPriceSimple(
  amount: number,
  currency: SupportedCurrency
): string {
  const symbol = CURRENCY_INFO[currency]?.symbol || "";
  const roundedAmount = roundForCurrency(amount, currency);
  const decimals = CURRENCY_INFO[currency]?.decimals ?? 0;

  return `${symbol}${roundedAmount.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

/**
 * 통화 코드 형태로 포맷 (예: "USD 3.14", "KRW 3,140")
 */
export function formatPriceWithCode(
  amount: number,
  currency: SupportedCurrency
): string {
  const roundedAmount = roundForCurrency(amount, currency);
  const decimals = CURRENCY_INFO[currency]?.decimals ?? 0;

  const formatted = roundedAmount.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return `${currency} ${formatted}`;
}

/**
 * 통화 기호 가져오기
 */
export function getCurrencySymbol(currency: SupportedCurrency): string {
  return CURRENCY_INFO[currency]?.symbol || currency;
}

/**
 * Locale의 **기본** 통화. 사용자가 통화를 따로 고르지 않았을 때만 쓴다.
 * 서버 렌더(ISR)·SEO(JSON-LD priceCurrency)처럼 URL만으로 결정돼야 하는 곳은
 * 항상 이 값을 쓰고, 화면 표시·결제는 CurrencyContext 의 selectedCurrency 를 쓴다.
 */
export function getCurrencyFromLocale(locale: string): SupportedCurrency {
  return LOCALE_CURRENCY_MAP[locale] || "USD";
}

/**
 * convertCurrency(source → target)가 실제로 환산하는지 — KRW 기준 환율표에서 KRW 가 아닌
 * 쪽은 rate 가 있어야 한다. 없으면 convertCurrency 는 원래 숫자를 그대로 돌려주므로,
 * 결제 금액 검증 전에 반드시 이걸로 막는다. (hasExchangeRate 는 USD 를 항상 true,
 * KRW 를 false 로 보므로 이 용도에 맞지 않다.)
 */
export function canConvertCurrency(
  source: SupportedCurrency,
  target: SupportedCurrency,
  rates: Partial<ExchangeRates>,
): boolean {
  if (source === target) return true;
  const ok = (c: SupportedCurrency) => c === "KRW" || (rates[c]?.rate ?? 0) > 0;
  return ok(source) && ok(target);
}

export function isSupportedCurrency(value: unknown): value is SupportedCurrency {
  return (
    typeof value === "string" &&
    (SUPPORTED_CURRENCIES as readonly string[]).includes(value)
  );
}

/**
 * 고객 결제 통화 결정 — 명시 요청(지원 통화일 때만) → locale 기본 통화.
 * 서버 가격 검증과 클라이언트가 같은 규칙을 쓰도록 한 곳에 둔다.
 */
export function resolveCustomerCurrency(
  requested: unknown,
  locale: string,
): SupportedCurrency {
  return isSupportedCurrency(requested) ? requested : getCurrencyFromLocale(locale);
}

/**
 * 환율이 있는 통화인지 확인
 */
export function hasExchangeRate(
  currency: SupportedCurrency,
  rates: Partial<ExchangeRates>
): boolean {
  if (currency === "USD") return true; // USD는 기준 통화
  return !!rates[currency]?.rate && rates[currency].rate > 0;
}

/**
 * USD 금액을 대상 통화로 변환하고 포맷
 */
export function convertAndFormat(
  amountUSD: number,
  currency: SupportedCurrency,
  rates: Partial<ExchangeRates>
): string {
  if (currency === "USD") {
    return formatPriceSimple(amountUSD, "USD");
  }

  const convertedAmount = convertFromUSD(amountUSD, currency, rates);

  // 환율이 없으면 USD로 표시 (이미 위에서 USD 체크 완료)
  if (convertedAmount === amountUSD) {
    return formatPriceSimple(amountUSD, "USD");
  }

  return formatPriceSimple(convertedAmount, currency);
}

/**
 * 환산 금액과 USD 원본 금액을 함께 표시
 *
 * @param amountUSD - 달러 금액 (DB에 저장된 가격)
 * @param currency - 표시할 대상 통화
 * @param rates - 환율 데이터
 * @returns { converted: 변환된 가격, original: USD 가격, showOriginal: 원본 표시 여부 }
 * @deprecated getDisplayPricesFromSource 사용 권장 (상품별 통화 지원)
 */
export function getDisplayPrices(
  amountUSD: number,
  currency: SupportedCurrency,
  rates: Partial<ExchangeRates>
): { converted: string; original: string; showOriginal: boolean } {
  return getDisplayPricesFromSource(amountUSD, "USD", currency, rates);
}

/**
 * 소스 통화에서 변환된 금액 표시 (상품별 통화 지원)
 *
 * @param amount - 원본 금액 (상품의 기준 통화로 저장된 가격)
 * @param sourceCurrency - 상품의 기준 통화 (예: JPY)
 * @param targetCurrency - 사용자가 선택한 표시 통화 (예: KRW)
 * @param rates - 환율 데이터
 * @returns { converted: 변환된 가격, original: 원본 가격, showOriginal: 원본 표시 여부 }
 */
export function getDisplayPricesFromSource(
  amount: number,
  sourceCurrency: SupportedCurrency,
  targetCurrency: SupportedCurrency,
  rates: Partial<ExchangeRates>
): { converted: string; original: string; showOriginal: boolean } {
  const original = formatPriceSimple(amount, sourceCurrency);

  // 동일 통화면 변환 불필요
  if (sourceCurrency === targetCurrency) {
    return { converted: original, original, showOriginal: false };
  }

  // 소스 통화 → 대상 통화로 변환
  const convertedAmount = convertCurrency(amount, sourceCurrency, targetCurrency, rates);
  const converted = formatPriceSimple(convertedAmount, targetCurrency);

  // 원본 표시 안 함 (사용자 요청)
  return { converted, original, showOriginal: false };
}

/**
 * 지원 통화 목록 가져오기 (환율 데이터 포함)
 */
export function getAvailableCurrencies(
  rates: Partial<ExchangeRates>
): Array<{
  code: SupportedCurrency;
  name: string;
  symbol: string;
  flag: string;
  available: boolean;
}> {
  return SUPPORTED_CURRENCIES.map((code) => ({
    code,
    name: CURRENCY_INFO[code].name,
    symbol: CURRENCY_INFO[code].symbol,
    flag: CURRENCY_INFO[code].flag,
    available: code === "USD" || hasExchangeRate(code, rates),
  }));
}
